import { Loader2, Lock, Play, Settings } from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import type { Meeting } from "@/lib/api";

/** How long after an occurrence is created it still counts as "live now" (Phase C reminders). */
const LIVE_WINDOW_MS = 15 * 60 * 1000;

function formatDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function formatDateTime(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString();
}

/**
 * A single meeting series on the dashboard's "Your meetings" section: title (+ lock badge for
 * private series), occurrence count, last-run date, a primary "Start this week's <title>"
 * action, and a "Manage" gear that opens the roster/agenda/privacy/occurrences dialog.
 *
 * Phase C: when the meeting is scheduled (`auto_create` + `next_run_at`), shows "Next: <local>";
 * when `last_occurrence_at` is within the last ~15 min, shows a "🔴 Live now — join" affordance.
 * The card only has summary fields from `GET /api/meetings` (no occurrence slug), so "join" opens
 * the same Manage dialog used elsewhere — its "Past occurrences" list is the one place that knows
 * each occurrence's room slug, so this is the cleanest path without widening the list payload.
 */
export function MeetingCard({
  meeting,
  starting,
  onStart,
  onManage,
}: {
  meeting: Meeting;
  starting?: boolean;
  onStart: () => void;
  onManage: () => void;
}) {
  const count = meeting.occurrence_count ?? 0;
  const lastRun = formatDate(meeting.last_occurrence_at);
  const nextRun = meeting.auto_create ? formatDateTime(meeting.next_run_at) : null;
  const lastOccurrenceMs = meeting.last_occurrence_at
    ? new Date(meeting.last_occurrence_at).getTime()
    : NaN;
  const isLiveNow = !Number.isNaN(lastOccurrenceMs) && Date.now() - lastOccurrenceMs < LIVE_WINDOW_MS;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <CardTitle className="flex min-w-0 items-center gap-1.5 text-sm">
            {meeting.is_private ? (
              <Lock className="size-3.5 shrink-0 text-muted" aria-label="Private meeting" />
            ) : null}
            <span className="truncate">{meeting.title}</span>
          </CardTitle>
          <button
            type="button"
            aria-label={`Manage ${meeting.title}`}
            title="Manage"
            onClick={onManage}
            className="shrink-0 rounded-md p-1.5 text-muted transition-colors hover:bg-elevated hover:text-text"
          >
            <Settings className="size-3.5" />
          </button>
        </div>
        <p className="text-xs text-muted">
          {count === 0 ? "No occurrences yet" : `${count} occurrence${count === 1 ? "" : "s"}`}
          {lastRun ? ` · last run ${lastRun}` : ""}
        </p>
        {nextRun ? <p className="text-xs text-muted">Next: {nextRun}</p> : null}
      </CardHeader>
      <CardContent className="flex flex-wrap items-center gap-2">
        <Button size="sm" onClick={onStart} disabled={starting}>
          {starting ? <Loader2 className="size-3.5 animate-spin" /> : <Play className="size-3.5" />}
          Start this week's {meeting.title}
        </Button>
        {isLiveNow ? (
          <Button size="sm" variant="outline" onClick={onManage}>
            🔴 Live now — join
          </Button>
        ) : null}
      </CardContent>
    </Card>
  );
}
